module Client.ScorablePassword

open System
open Shared.Zxcvbn
open Illy.SafeStack.Client
open Elmish
open Fable.React
open Fable.React.Props
open Shared.Zxcvbn
open SafeStack.Client

type Settings = 
    {
        Label: string
        Config: PasswordConfig
    }

module Settings = 
    let defaults = 
        {
            Label = "Password"
            Config = PasswordConfig.defaults
        }

type Model = 
    {
        Password: Text.Model
        ExamplePassword: string
        Score: PasswordScore option
        Settings: Settings
        DebounceScoreUpdate: (string option) Debounce.Model
    }

type Msg = 
    | PasswordMsg of Text.Msg
    | DebounceScoreUpdateMsg of (string option) Debounce.Msg
    | UpdateScore of string option 
    | UpdateZxcvbnList of ZxcvbnList

module Initialisations =
    let initPassword label maxLength value =
        {Text.CustomSettings.defaultOptions() with TextType = Text.TextType.Password}
        |> Text.init "Password" label (Some (sprintf "Enter %s" label)) maxLength true value

let init (settings: Settings) value = 
    let str = value |> Option.defaultValue ""
    {
        Password = Initialisations.initPassword settings.Label (float (settings.Config.PasswordStrength.MaxLength |> Option.defaultValue 250)) value
        ExamplePassword = ExamplePasswordGenerator.newPassword settings.Config.PasswordStrength
        Score = 
            if String.IsNullOrEmpty str then None 
            else Zxcvbn.scorePassword settings.Config.ZxcvbnList str |> Some 
        Settings = settings
        DebounceScoreUpdate = Debounce.init (TimeSpan.FromMilliseconds 300.) None
    }

let toString (model: Model) = Text.toString model.Password

let resetValidations (model: Model) = 
    {model with Password = Text.resetValidations model.Password}

let setValidations model errors warnings =
    {model with Password = Text.setValidations model.Password errors warnings}

let update msg model = 
    match msg with 
    | PasswordMsg cMsg ->
        let m, c = Text.update cMsg model.Password
        let hasSearchStrChanged = Text.toString m <> Text.toString model.Password
        {model with Password = m}, Cmd.batch [
            yield Cmd.map PasswordMsg c
            if hasSearchStrChanged then yield Debounce.inputCmd (Text.toString m) DebounceScoreUpdateMsg
        ]
    | DebounceScoreUpdateMsg dMsg -> 
        Debounce.updateWithCmd
            dMsg
            DebounceScoreUpdateMsg
            model.DebounceScoreUpdate
            (fun x -> 
                { model with
                    DebounceScoreUpdate = x 
                })
            (UpdateScore >> Cmd.ofMsg)
    | UpdateScore pwd -> 
        let str = pwd |> Option.defaultValue ""
        {model with 
            Score = 
                if String.IsNullOrEmpty str then None 
                else Zxcvbn.scorePassword model.Settings.Config.ZxcvbnList str |> Some 
        }, Cmd.none
    | UpdateZxcvbnList l -> 
        {model with
            Settings =
                { model.Settings with
                    Config = {model.Settings.Config with ZxcvbnList = l}
                }
        },
        model.Password |> Text.toString |> UpdateScore |> Cmd.ofMsg

let private pStrengthSetting minValue maxValue setting =
    match minValue, maxValue with
    | min, Some max when min = max -> 
        sprintf "It must have %d %s%s." min setting (if min <> 1 then "s" else "") |> Some
    | min, Some max when min > 0 && max > 0 ->
        sprintf "It must have between %d and %d %ss" min max setting |> Some
    | min, _ when min > 0 ->
        sprintf "It must have at least %d %s%s." min setting (if min <> 1 then "s" else "") |> Some
    | _, Some max when max > 0 ->
        sprintf "It must have at most %d %s%s." max setting (if max <> 1 then "s" else "") |> Some
    | _, _ -> None 

let passwordPolicyTooltip (c: PasswordStrengthConfig) examplePassword =
    [
        p [] [
            str "Your password should follow the following rules:"
        ]
        ul [] [
            yield!
                [
                    pStrengthSetting c.MinLength c.MaxLength "character"
                    pStrengthSetting c.MinNumeric c.MaxNumeric "number"
                    pStrengthSetting c.MinSpecial c.MaxSpecial "special character"
                    pStrengthSetting c.MinUpper c.MaxUpper "upper case character"
                    pStrengthSetting c.MinLower c.MaxLower "lower case character"
                    pStrengthSetting c.MinWhitespace c.MaxWhitespace "space"
                ]
                |> List.choose id
                |> List.map (str >> List.singleton >> li [])
        ]
        p [] [
            str "An example password using your current policy is: "
            br []
            b [] [ str examplePassword ]
        ]
    ]

let view loading model dispatch = 
    [
        yield jQueryUI.inputGroupWithClasses model.Password.CustomSettings.InputGroupCSSClasses [
            let control = model.Password.Control
            div [
                ClassName "label label-inline"
            ] [
                jQueryUI.labelWithMandatory control.Id control.Label control.Error control.Warning loading control.Mandatory
                str " "
                FontAwesome.solid "info" [ FontAwesome.Fixed ] [ Style [ Cursor "pointer" ] ]
                |> Tooltip.createInteractive (passwordPolicyTooltip model.Settings.Config.PasswordStrength model.ExamplePassword)
            ]
            Text.textInput loading model.Password (PasswordMsg >> dispatch)
        ]
        match model.Score with 
        | None -> ()
        | Some score -> 
            yield Zxcvbn.renderPwdStrengthBar model.Settings.Config.MinScore score
    ]

let getScore (m: Model) = m.Score